import { ComponentType, useCallback, useEffect, useState } from 'react';
import { FederatedSearchNotificationsContextValue } from './federated-search-context';
import { lazyLoadFederatedSearchModule } from './federated-search-remote-monitor';

/**
 * Bridges the USx `useUnreadFederatedQueryCount` hook — which must run inside
 * `FederatedSearchProvider` — into CAD's `FederatedSearchNotificationsContext`.
 *
 * Wraps `markAllQueriesAsRead` to suppress the badge while the PATCH is in flight
 * and for a short grace period after it resolves, absorbing stale refetches that
 * race the PATCH.
 *
 * Suppression phases:
 *   idle          – badge reflects raw USx value
 *   patch-in-flight – PATCH in progress; badge locked at 0
 *   grace-period  – PATCH resolved; badge held at 0 until USx count reaches 0,
 *                   then held for MARK_READ_GRACE_PERIOD_MS to absorb stream-triggered
 *                   refetches that return stale count=1 before the server commit is visible
 */

// Empirically: stream-triggered refetches arrive 150–400ms after the PATCH resolves.
// Holding the badge at 0 for this window prevents a visible flicker.
const MARK_READ_GRACE_PERIOD_MS = 400;

const MAX_SUPPRESS_MS = 3000;

export type SuppressState = 'idle' | 'patch-in-flight' | 'grace-period';

export type UseNotificationsSuppressProps = {
    unreadQueryCount: number;
    unreadQueriesIncludeSpecialResult: boolean;
    markAllQueriesAsRead: () => Promise<void>;
};

export type UseNotificationsSuppressResult = {
    effectiveCount: number;
    effectiveSpecial: boolean;
    wrappedMarkAllQueriesAsRead: () => Promise<void>;
    suppressState: SuppressState;
};

/**
 * Extracted for unit testing. Contains all suppression logic; the component is
 * a thin wrapper that feeds USx hook values in and pushes the results to context.
 */
export function useNotificationsSuppress({
    unreadQueryCount,
    unreadQueriesIncludeSpecialResult,
    markAllQueriesAsRead,
}: UseNotificationsSuppressProps): UseNotificationsSuppressResult {
    const [suppressState, setSuppressState] = useState<SuppressState>('idle');

    const wrappedMarkAllQueriesAsRead = useCallback(async () => {
        setSuppressState('patch-in-flight');
        try {
            await markAllQueriesAsRead();
        } catch {
            setSuppressState('idle');
            return;
        }
        setSuppressState('grace-period');
    }, [markAllQueriesAsRead]);

    useEffect(() => {
        if (suppressState !== 'grace-period' || unreadQueryCount !== 0) return;
        const timer = setTimeout(() => setSuppressState('idle'), MARK_READ_GRACE_PERIOD_MS);
        return () => clearTimeout(timer);
    }, [suppressState, unreadQueryCount]);

    useEffect(() => {
        if (suppressState === 'idle') return;
        const timer = setTimeout(() => setSuppressState('idle'), MAX_SUPPRESS_MS);
        return () => clearTimeout(timer);
    }, [suppressState]);

    const isSuppressing = suppressState !== 'idle';

    return {
        effectiveCount: isSuppressing ? 0 : unreadQueryCount,
        effectiveSpecial: isSuppressing ? false : unreadQueriesIncludeSpecialResult,
        wrappedMarkAllQueriesAsRead,
        suppressState,
    };
}

type NotificationsSyncProps = {
    onUpdate: (v: FederatedSearchNotificationsContextValue) => void;
};

export const FederatedSearchNotificationsSync = lazyLoadFederatedSearchModule<
    ComponentType<NotificationsSyncProps>
>(
    'useUnreadFederatedQueryCount',
    () => import('federated-search/useUnreadFederatedQueryCount'),
    (module) => {
        const useUnreadFederatedQueryCount = module.useUnreadFederatedQueryCount as () => UseNotificationsSuppressProps;

        return function NotificationsSync({ onUpdate }: NotificationsSyncProps) {
            const {
                unreadQueryCount,
                unreadQueriesIncludeSpecialResult,
                markAllQueriesAsRead,
            } = useUnreadFederatedQueryCount();

            const {
                effectiveCount,
                effectiveSpecial,
                wrappedMarkAllQueriesAsRead,
            } = useNotificationsSuppress({
                unreadQueryCount,
                unreadQueriesIncludeSpecialResult,
                markAllQueriesAsRead,
            });

            useEffect(() => {
                onUpdate({
                    unreadQueryCount: effectiveCount,
                    unreadQueriesIncludeSpecialResult: effectiveSpecial,
                    markAllQueriesAsRead: wrappedMarkAllQueriesAsRead,
                });
            }, [effectiveCount, effectiveSpecial, wrappedMarkAllQueriesAsRead, onUpdate]);

            return null;
        };
    },
    () => null
);
